import { useSyncExternalStore, useState, useCallback, useMemo } from "react"
import { getAnalyticsService } from "../services/analytics"
import { getCookieConsent, detectConsentRegion, isGlobalPrivacyControlEnabled } from "../utils/cookie"

/**
 * @typedef {'pending' | 'accepted' | 'rejected'} ConsentStatus
 * @typedef {'us' | 'canada' | 'international'} ConsentRegion
 */

/**
 * @typedef {Object} CookieConsentResult
 * @property {ConsentStatus} status - Current consent status
 * @property {boolean} showBanner - Whether to show the banner
 * @property {ConsentRegion} region - Detected user region
 * @property {() => void} acceptAll - Accept all cookies
 * @property {() => void} rejectNonEssential - Reject non-essential cookies
 * @property {() => void} closeBanner - Close banner without changing consent
 */

/**
 * Hook for managing cookie consent state.
 * Subscribes to AnalyticsService for consent status.
 * Manages banner visibility as local UI state.
 *
 * @returns {CookieConsentResult} Cookie consent state and actions
 */
export function useCookieConsent() {
  const analyticsService = getAnalyticsService()

  const { status } = useSyncExternalStore(analyticsService.subscribe, analyticsService.getSnapshot)

  // Hide banner if:
  // - consent is not required (env flag disabled)
  // - user has GPC enabled (automatic opt-out)
  // - user has existing consent cookie
  const [showBanner, setShowBanner] = useState(() => {
    if (!analyticsService.config.consentRequired) {
      return false
    }
    return getCookieConsent() === null && !isGlobalPrivacyControlEnabled()
  })
  const region = useMemo(() => detectConsentRegion(), [])

  const acceptAll = useCallback(() => {
    analyticsService.optIn()
    setShowBanner(false)
  }, [analyticsService])

  const rejectNonEssential = useCallback(() => {
    analyticsService.optOut()
    setShowBanner(false)
  }, [analyticsService])

  const closeBanner = useCallback(() => {
    // US: Implicit consent acceptable (CCPA allows opt-out model)
    // International: Explicit consent required (GDPR requires opt-in)
    if (region === "us") {
      acceptAll()
    } else {
      rejectNonEssential()
    }
  }, [region, acceptAll, rejectNonEssential])

  return {
    status,
    showBanner,
    region,
    acceptAll,
    rejectNonEssential,
    closeBanner,
  }
}
